import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if it's iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isAppInstalled = (navigator as any).standalone || isStandalone;

    if (isAppInstalled) {
      return; // Don't show install prompt if already installed
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstallPrompt(true);
    };

    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS, show install prompt after a delay if not installed
    if (isIOSDevice && !isAppInstalled) {
      const timer = setTimeout(() => {
        const hasShownIOSPrompt = localStorage.getItem('ios-install-prompt-shown');
        if (!hasShownIOSPrompt) {
          setShowInstallPrompt(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Auto-dismiss the prompt after 10 seconds
  useEffect(() => {
    if (showInstallPrompt) {
      const autoDismissTimer = setTimeout(() => {
        setShowInstallPrompt(false);
        if (isIOS) {
          localStorage.setItem('ios-install-prompt-shown', 'true');
        }
      }, 10000); // 10 seconds

      return () => clearTimeout(autoDismissTimer);
    }
  }, [showInstallPrompt, isIOS]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
      }
    } else if (isIOS) {
      // For iOS, we can't programmatically trigger install
      localStorage.setItem('ios-install-prompt-shown', 'true');
      alert(
        'To install StayMiddle on your device:\n\n' +
        '1. Tap the Share button at the bottom of the screen\n' +
        '2. Scroll down and tap "Add to Home Screen"\n' +
        '3. Tap "Add" to confirm'
      );
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    if (isIOS) {
      localStorage.setItem('ios-install-prompt-shown', 'true');
    }
  };

  if (!showInstallPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-primary text-primary-foreground border border-primary/20 rounded-lg shadow-xl p-4 z-50 animate-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <img src="/staymiddle-logo-icon-white.png" alt="StayMiddle" className="w-8 h-8 mr-2" />
            <h3 className="font-semibold text-sm text-primary-foreground">Install StayMiddle</h3>
          </div>
          <p className="text-xs text-primary-foreground/90 mb-3">
            {isIOS 
              ? 'Install our app for a better experience and offline access.'
              : 'Get quick access and work offline. Install our app!'
            }
          </p>
          <div className="flex gap-2">
            <Button
              onClick={handleInstallClick}
              size="sm"
              variant="secondary"
              className="flex items-center gap-1 text-xs bg-white text-primary hover:bg-white/90"
            >
              <Download className="w-3 h-3" />
              {isIOS ? 'Install Guide' : 'Install'}
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              size="sm"
              className="text-xs bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
            >
              Later
            </Button>
          </div>
          <div className="mt-2">
            <p className="text-xs text-primary-foreground/70">Auto-closes in 10 seconds</p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-2 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
          aria-label="Dismiss install prompt"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
} 